import pontoLogic from "./ponto/pontoLogic.json";
import { ActivityMonitorView, ActivityTrendView } from "./ponto/pontoUi";
import pacsanaLogic from "./pacsana/pacsanaLogic.json";
import {
  ActivityMonitorView as PacsanaMonitorView,
  ActivityTrendView as PacsanaTrendView,
} from "./pacsana/pacsanaUi";

/**
 * Activity-monitoring sensor-type registry.
 * ----------------------------------------------------------------------------
 * The Activity Monitoring screens are sensor-type agnostic: they resolve which
 * display logic + view components to render from this registry, keyed by the
 * sensor type reported by the backend (`sensor.type` on the normalized payload,
 * or `sensorType` on the sensor mapping).
 *
 * To ADD A NEW SENSOR TYPE later:
 *   1. Build its display-logic JSON (labels/colors/copy/order) + its two view
 *      components: a main
 *      `MonitorView({ data, logic, onBack, onSensorMappingChange })`
 *      and a `TrendView({ data, logic, onBack })`.
 *   2. Add an entry below with a unique `id`, metadata, `logic`, `MonitorView`
 *      and `TrendView`.
 *   3. Ensure the backend reports that `id` as the sensor's `type` (and returns
 *      that sensor type's normalized data shape).
 * No changes to the screens, services, or assign flow are required.
 *
 * See docs/sensor-types.md for the full guide.
 */
export const SENSOR_TYPES = {
  pontosense: {
    id: "pontosense",
    label: "Pontosense Radar",
    manufacturer: "Pontosense",
    description:
      "mmWave radar sensor for fall detection and activity monitoring.",
    capabilities: [
      "fall_detection",
      "activity_trend",
      "abnormality_detection",
      "device_health",
    ],
    // Display logic (labels/colors/copy/order) for this sensor type.
    logic: pontoLogic,
    // Screen renderers for this sensor type.
    MonitorView: ActivityMonitorView,
    TrendView: ActivityTrendView,
  },
  pacsana: {
    id: "pacsana",
    label: "PacSana Bracelet",
    manufacturer: "PacSana",
    description:
      "Wearable bracelet for fall detection and daily activity monitoring.",
    capabilities: [
      "fall_detection",
      "activity_trend",
      "abnormality_detection",
      "device_health",
      "wear_status",
    ],
    logic: pacsanaLogic,
    MonitorView: PacsanaMonitorView,
    TrendView: PacsanaTrendView,
  },
};

/** Sensor type used for the type-agnostic setup ("attach a sensor") UI. */
export const DEFAULT_SENSOR_TYPE = "pontosense";

/**
 * Resolve a sensor-type config for a mapped sensor. Returns `undefined` when the
 * id is missing or unknown — callers must treat that as a wrong/unsupported
 * sensor (surface an error) rather than silently rendering a default. For the
 * unmapped setup state (no sensor bound yet), use getDefaultSensorType().
 * @param {string} [type]
 */
export const getSensorType = (type) =>
  SENSOR_TYPES[String(type || "").toLowerCase()];

/** Default renderer for the type-agnostic setup state (no sensor mapped yet). */
export const getDefaultSensorType = () => SENSOR_TYPES[DEFAULT_SENSOR_TYPE];

/** All registered sensor types, e.g. for a future "add sensor" type picker. */
export const listSensorTypes = () => Object.values(SENSOR_TYPES);
